import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { TRANSLATIONS } from "./translations";

const I18nContext = createContext(null);

const LS_KEY = "mva_lang";

function detectInitialLang() {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(LS_KEY);
  if (stored === "en" || stored === "es") return stored;
  const browser = (window.navigator?.language || "").toLowerCase();
  return browser.startsWith("es") ? "es" : "en";
}

function interpolate(template, vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : `{${k}}`
  );
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEY, lang);
      document.documentElement.lang = lang;
    } catch (e) {
      // localStorage access can fail in private mode / strict cookie settings.
      // We log it for visibility but fall back silently — language still works
      // within the current session via React state.
      // eslint-disable-next-line no-console
      console.warn("Failed to persist language preference:", e?.message);
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (next === "en" || next === "es") setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
      const raw = dict[key] ?? TRANSLATIONS.en[key] ?? key;
      return interpolate(raw, vars);
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}

export function useT() {
  return useI18n().t;
}
